import { Link, NavLink, Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getPlatformStatus } from '@/api/auth';
import { useAuth, getRefreshToken } from '@/auth/store';
import { logout as apiLogout } from '@/api/auth';
import ErrorBoundary from '@/components/ErrorBoundary';
import {
  PLATFORM_SECTION_GROUPS,
  DEFAULT_PLATFORM_SECTION,
} from '@/pages/platform/platformSections';

/**
 * Platform Admin Блок A — окремий layout для super-admin розділу /platform.
 *
 * Відмінне від AppLayout оформлення (темна шапка «⚙ Platform Admin» +
 * попередження про вплив на ВСІХ клієнтів), щоб платформенний власник
 * чітко усвідомлював, що працює з глобальними системними даними, а не з
 * даними окремого tenant'а.
 *
 * Гейт: при монтуванні читає GET /auth/platform-status. Якщо user НЕ
 * платформенний власник — redirect на /. Поки статус завантажується —
 * показуємо нейтральний placeholder (не блимаємо контентом).
 */
export default function PlatformLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const clear = useAuth((s) => s.clear);
  const status = useQuery({
    queryKey: ['platform-status'],
    queryFn: getPlatformStatus,
    retry: false,
  });

  // Активний розділ системних даних — з query-параметра ?tab=.
  const onSystemData = location.pathname.startsWith('/platform/system-data');
  const activeTab = (onSystemData && searchParams.get('tab')) || DEFAULT_PLATFORM_SECTION;

  async function handleLogout() {
    const r = getRefreshToken();
    if (r) {
      try {
        await apiLogout(r);
      } catch {
        /* ignore */
      }
    }
    clear();
    navigate('/login', { replace: true });
  }

  // Поки чекаємо відповідь — нейтральний екран.
  if (status.isLoading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', color: '#94a3b8' }}>
        Завантаження…
      </div>
    );
  }

  // Немає доступу (не платформенний власник або помилка) — на головну.
  if (!status.data?.is_platform_owner) {
    navigate('/', { replace: true });
    return null;
  }

  return (
    <div className="app">
      <header
        className="app__header"
        style={{ background: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)', color: '#fff' }}
      >
        <h1 style={{ color: '#fff' }}>⚙ Platform Admin</h1>
        <div className="right">
          <span
            style={{
              background: 'rgba(255,255,255,0.18)',
              padding: '0.25rem 0.6rem',
              borderRadius: '6px',
              fontSize: '0.8rem',
            }}
          >
            ⚠ Глобальні системні дані — впливає на ВСІХ клієнтів
          </span>
          <button onClick={handleLogout}>Вихід</button>
        </div>
      </header>
      <aside className="app__sidebar">
        {PLATFORM_SECTION_GROUPS.map((group) => (
          <div key={group.title}>
            <div className="section">{group.title}</div>
            {group.sections.map((s) => (
              <Link
                key={s.id}
                to={`/platform/system-data?tab=${s.id}`}
                className={
                  onSystemData && activeTab === s.id ? 'active' : undefined
                }
                style={
                  onSystemData && activeTab === s.id
                    ? { background: 'rgba(109,40,217,0.12)', fontWeight: 600 }
                    : undefined
                }
              >
                {s.label}
              </Link>
            ))}
          </div>
        ))}
        <NavLink to="/" style={{ marginTop: '1.25rem', opacity: 0.8 }}>
          ← Назад у кабінет
        </NavLink>
      </aside>
      <main className="app__main">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
